import { useEffect, useState } from "react";
import { CreditCard, ReceiptText, Truck, CheckCircle2, TrendingUp, Percent } from "lucide-react";
import { api, money } from "@/lib/api";
import { Link } from "react-router-dom";
import { KpiCard, KpiCardSkeleton } from "./components/KpiCard";
import { DashboardHeader } from "./components/DashboardHeader";
import { NeedsAttention } from "./components/NeedsAttention";
import { useDashboardCache } from "./hooks/useDashboardCache";
import { QuickActionsRow } from "./components/QuickActionsRow";

const inr = (n) => `₹${money(n || 0)}`;

/**
 * CashierDashboard — billing + collections focus.
 */
export default function CashierDashboard({ user, isCashier = true }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const summary = useDashboardCache("fender_cash_summary", "/dashboard/summary", { params: { range: "today" } });
  const attention = useDashboardCache("fender_cash_attention", "/dashboard/action-centre");

  useEffect(() => {
    api.get("/invoices").then((r) => setInvoices(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const outstanding = invoices.filter((i) => i.status === "unpaid" || i.status === "partial");
  const paidToday = invoices.filter(
    (i) => i.status === "paid" && ((i.updated_at || i.created_at || "").slice(0, 10) === today)
  );
  const partial = invoices.filter((i) => i.status === "partial");
  const readyForBill = summary.data?.ready_for_delivery || 0;
  const readyWithPending = summary.data?.ready_pending_payment || 0;
  const totalDue = outstanding.reduce((s, i) => s + Number(i.balance || 0), 0);
  const collectedToday = paidToday.reduce((s, i) => s + Number(i.paid_amount || i.total || 0), 0);

  const kpis = [
    { key: "ready-bill", label: "Ready for Billing", value: readyForBill, hint: "Deliver + close invoice", Icon: ReceiptText, tone: "frosted", to: "/job-cards?status=completed", testId: "cash-kpi-billing" },
    { key: "pending", label: "Payments Pending", value: inr(totalDue), hint: `${outstanding.length} invoice${outstanding.length === 1 ? "" : "s"}`, Icon: CreditCard, tone: "red", to: "/invoices", testId: "cash-kpi-pending" },
    { key: "received", label: "Received Today", value: inr(collectedToday), hint: `${paidToday.length} settled`, Icon: TrendingUp, tone: "green", testId: "cash-kpi-received" },
    { key: "partial", label: "Partially Paid", value: partial.length, hint: "Balance outstanding", Icon: Percent, tone: "orange", to: "/invoices?status=partial", testId: "cash-kpi-partial" },
    { key: "ready-pay", label: "Ready + Pay Pending", value: readyWithPending, hint: "Collect before delivery", Icon: Truck, tone: "red", to: "/job-cards?status=completed", testId: "cash-kpi-readypay" },
    { key: "closed", label: "Settled Today", value: paidToday.length, hint: "Great work", Icon: CheckCircle2, tone: "green", testId: "cash-kpi-closed" },
  ];

  const quickActions = [
    { key: "record-pay", label: "Record Payment", Icon: CreditCard, url: "/invoices", tone: "primary" },
    { key: "create-inv", label: "Create Invoice", Icon: ReceiptText, url: "/invoices" },
    { key: "outstanding", label: "Outstanding", Icon: TrendingUp, url: "/invoices?status=unpaid" },
    { key: "partial-list", label: "Partial", Icon: Percent, url: "/invoices?status=partial" },
    { key: "customers", label: "Customers", Icon: CheckCircle2, url: "/customers" },
  ];

  const hi = (user?.name || "").split(" ")[0] || (isCashier ? "Cashier" : "Accountant");
  return (
    <div className="space-y-6" data-testid={isCashier ? "cashier-dashboard-v3" : "accountant-dashboard-v3"}>
      <DashboardHeader
        title={isCashier ? "Cash Desk" : "Finance Dashboard"}
        subtitle={`Collect, reconcile and follow up, ${hi}.`}
        range="today"
        onRangeChange={() => {}}
        onRefresh={() => { summary.reload(); attention.reload(); }}
        onCustomize={() => {}}
        onFilter={() => {}}
        lastSyncedAt={summary.lastSyncedAt}
        loading={summary.loading || loading}
      />
      <QuickActionsRow actions={quickActions} />
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {kpis.map(({ key, ...rest }) => (summary.loading && !summary.data ? <KpiCardSkeleton key={key} /> : <KpiCard key={key} {...rest} />))}
      </div>
      <NeedsAttention items={(attention.data?.items || []).filter((it) => ["payment", "delivery"].includes(it.category))} />

      {/* Outstanding invoices */}
      <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid="cash-outstanding">
        <header className="px-5 py-3.5 border-b border-[#F1FAEE] flex items-center justify-between">
          <div>
            <h3 className="text-[15px] font-semibold text-[#1d3557]">Outstanding invoices</h3>
            <p className="text-[11.5px] text-[#667085] mt-0.5">Highest balances first.</p>
          </div>
          <Link to="/invoices" className="text-[12px] font-semibold text-[#457b9d] hover:text-[#e63946]" data-testid="cash-outstanding-viewall">
            View all →
          </Link>
        </header>
        {outstanding.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">All invoices settled.</div>
        ) : (
          <ul className="divide-y divide-[#F1FAEE]">
            {outstanding.sort((a, b) => (b.balance || 0) - (a.balance || 0)).slice(0, 10).map((i) => (
              <li key={i.id}>
                <Link
                  to="/invoices"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-[#F7F9FC]"
                  data-testid={`cash-invoice-${i.invoice_number}`}
                >
                  <div className="w-8 h-8 rounded-md bg-[#F79009]/10 text-[#F79009] grid place-items-center shrink-0">
                    <ReceiptText size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-mono font-semibold text-[#1d3557] truncate">{i.invoice_number}</div>
                    <div className="text-[11px] text-[#667085]">Total {inr(i.total)} · Paid {inr(i.paid_amount)}</div>
                  </div>
                  <div className="text-[14px] font-bold font-mono text-[#e63946]">{inr(i.balance)}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
